import { useEffect, useRef, useState } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';
import HowItWorksProgress from './HowItWorksProgress';

gsap.registerPlugin(ScrollTrigger);

/** Dimmed treatment for steps that have not activated yet — same values as
 *  `.processing-dull` in globals.css, the project's existing inactive look. */
const DIMMED = '[filter:brightness(0.4)_saturate(0.5)]';

const HowItWorks = () => {
    const sectionRef = useRef<HTMLElement>(null);

    const steps = [
        {
            number: 1,
            title: "Input Your Problem ",
            description: "Scan your question or type it manually. Works smoothly with equations, graphs & diagrams."
        },
        {
            number: 2,
            title: "Eddy AI Starts Processing ",
            description: "Eddy AI analyzes the problem, understands the concept, and starts solving it."
        },
        {
            number: 3,
            title: "Get Instant Solution",
            description: "A clear and optimised solution is generated in few seconds, that is simple and easy to understand."
        }
    ];

    /**
     * Drives the sequential reveal: -1 before the section is reached, then the
     * index of the step currently filling. A step is dimmed while activeStep is
     * below its index and stays complete once it has passed.
     *
     * Reduced motion starts at the final index instead: a 0s transition emits no
     * transitionend, so the onTransitionEnd chain below would otherwise stall.
     */
    const [activeStep, setActiveStep] = useState(() =>
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ? steps.length - 1 : -1
    );

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            // Badge slides in from left
            tl.fromTo('.hiw-badge', { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' });

            // Title fades up
            tl.fromTo('.hiw-title', { opacity: 0, y: 25 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, '-=0.2');

            // Steps stagger in from left
            tl.fromTo(
                '.hiw-step',
                { opacity: 0, x: -40 },
                { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out', stagger: 0.2 },
                '-=0.2'
            );

            // Right image slides in from right
            tl.fromTo('.hiw-image', { opacity: 0, x: 60 }, { opacity: 1, x: 0, duration: 0.8, ease: 'power2.out' }, '-=0.8');

            // Kick off the step sequence once the panel has finished sliding in.
            // toggleActions 'play none none none' means this fires exactly once.
            tl.call(() => setActiveStep((step) => (step < 0 ? 0 : step)));
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} id="how-it-works" className="relative z-10 px-4 sm:px-6 md:px-10 py-12 sm:py-16 md:py-24 lg:py-32 max-w-[1440px] mx-auto">
            {/* Main Card Container */}
            <div className="relative rounded-[14px] sm:rounded-[16px] md:rounded-[18px] p-5 sm:p-7 md:p-10 lg:p-10 bg-gradient-to-b from-[#8E57FA]/50 to-[#6B3FD9]/20 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full animate-pulse" />
                    <div className="absolute top-20 right-20 w-2 h-2 bg-white rounded-full animate-pulse delay-100" />
                    <div className="absolute bottom-20 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse delay-200" />
                    <div className="absolute bottom-10 right-1/3 w-2 h-2 bg-white rounded-full animate-pulse delay-300" />
                </div>

                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-center">
                    {/* Left Side - Steps */}
                    <div className="space-y-5 sm:space-y-7 md:space-y-10 px-0 sm:px-2 md:px-4">
                        {/* Badge */}
                        <div className="hiw-badge inline-block" style={{ opacity: 0 }}>
                            <span
                                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#6B41BC] rounded-full text-white backdrop-blur-sm"
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 500,
                                    fontSize: "clamp(11px, 2.5vw, 14px)",
                                    letterSpacing: "0.5px",
                                }}
                            >
                                How it Works
                            </span>
                        </div>

                        {/* Title */}
                        <h2
                            className="hiw-title text-white"
                            style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 600,
                                fontSize: "clamp(22px, 5vw, 36px)",
                                lineHeight: "clamp(28px, 6vw, 44px)",
                                letterSpacing: "0px",
                                opacity: 0,
                            }}
                        >
                            From Question to Mastery in Seconds
                        </h2>

                        {/* Steps List */}
                        <div className="space-y-4 sm:space-y-6 md:space-y-8">
                            {steps.map((step, index) => (
                                <div
                                    key={step.number}
                                    className={`hiw-step flex gap-3 sm:gap-4 transition-[filter] duration-[600ms] ease-out motion-reduce:transition-none ${activeStep < index ? DIMMED : ''}`}
                                    style={{ opacity: 0 }}
                                >
                                    {/* Number */}
                                    <div className="flex-shrink-0">
                                        <div
                                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-[#321E58] backdrop-blur-sm flex items-center justify-center text-white"
                                            style={{
                                                fontFamily: "'Poppins', sans-serif",
                                                fontWeight: 600,
                                                fontSize: "clamp(14px, 3.5vw, 20px)",
                                            }}
                                        >
                                            {step.number}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 space-y-1 sm:space-y-2">
                                        <h3
                                            className="text-white"
                                            style={{
                                                fontFamily: "'Roboto', sans-serif",
                                                fontWeight: 600,
                                                fontSize: "clamp(14px, 3.5vw, 18px)",
                                                lineHeight: "clamp(20px, 4vw, 24px)",
                                            }}
                                        >
                                            {step.title}
                                        </h3>
                                        <p
                                            className="text-[#DCCBFD]"
                                            style={{
                                                fontFamily: "'Roboto', sans-serif",
                                                fontWeight: 400,
                                                fontSize: "clamp(12px, 2.8vw, 15px)",
                                                lineHeight: "clamp(18px, 3.5vw, 21px)",
                                                letterSpacing: "0.3px",
                                            }}
                                        >
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Side - Progress Cards */}
                    <div className="hiw-image flex items-center justify-center lg:justify-end" style={{ opacity: 0 }}>
                        <HowItWorksProgress
                            activeStep={activeStep}
                            onStepComplete={() =>
                                setActiveStep((step) => Math.min(step + 1, steps.length - 1))
                            }
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
